import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Award,
  Globe,
  Users,
  Target,
  Lightbulb,
  Shield,
  Code2,
  Brain,
  GraduationCap,
  Building2,
  MapPin,
  Calendar,
  TrendingUp,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Star,
  Sparkles,
  Heart,
  Briefcase,
  BookOpen,
  Zap,
  Eye,
  HandshakeIcon,
  Trophy,
  Medal
} from 'lucide-react';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { useTranslation } from 'react-i18next';

// Icon mappings for timeline
const timelineIcons: Record<string, typeof Rocket> = {
  '2017': Rocket,
  '2018': Globe,
  '2019': TrendingUp,
  '2020': Brain,
  '2021': Building2,
  '2022': GraduationCap,
  '2023': MapPin,
  '2024': Code2
};

// Icon mappings for awards
const awardIcons: Record<string, typeof Trophy> = {
  tubitak: Trophy,
  ieee: BookOpen,
  teknofest: Medal,
  google: Award,
  aws: Award,
  iso: Shield
};

// Icon mappings for values
const valueIcons: Record<string, { icon: typeof Eye; color: string }> = {
  hiddenHero: { icon: Eye, color: 'from-purple-500 to-violet-500' },
  innovation: { icon: Lightbulb, color: 'from-amber-500 to-orange-500' },
  reliability: { icon: Shield, color: 'from-green-500 to-emerald-500' },
  partnership: { icon: HandshakeIcon, color: 'from-blue-500 to-cyan-500' }
};

// Icon mappings for team highlights
const teamIcons: Record<string, typeof GraduationCap> = {
  academic: GraduationCap,
  experience: Briefcase,
  publications: BookOpen,
  certifications: Award
};

// Animated Globe Component
function AnimatedGlobe() {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-72 h-72 mx-auto">
      {/* Outer glow */}
      <div className="absolute inset-0 rounded-full bg-purple-500/10 blur-2xl" />

      {/* Globe base */}
      <div className="absolute inset-4 rounded-full bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 border border-purple-500/30 backdrop-blur-sm" />

      {/* Latitude lines */}
      <div className="absolute inset-8 rounded-full border border-purple-500/15" />
      <div className="absolute inset-14 rounded-full border border-purple-500/10" />
      <div className="absolute inset-20 rounded-full border border-purple-500/10" />

      {/* Longitude line (vertical) */}
      <div className="absolute top-4 bottom-4 left-1/2 w-px bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />

      {/* Equator line */}
      <div className="absolute left-4 right-4 top-1/2 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />

      {/* Orbiting markers representing global reach */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * 60 + rotation) * (Math.PI / 180);
        const radius = 42;
        const x = 50 + radius * Math.cos(angle);
        const y = 50 + radius * Math.sin(angle);
        const scale = 0.6 + 0.4 * Math.sin(angle); // Creates depth effect
        const opacity = 0.4 + 0.6 * ((Math.sin(angle) + 1) / 2);
        return (
          <div
            key={i}
            className="absolute w-2.5 h-2.5 rounded-full shadow-lg transition-all duration-100"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity,
              background: `linear-gradient(135deg, #A78BFA, #8B5CF6)`,
              boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)'
            }}
          />
        );
      })}

      {/* Center icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 flex items-center justify-center">
          <Globe className="text-purple-400" size={32} />
        </div>
      </div>

      {/* Floating particles */}
      {[...Array(8)].map((_, i) => {
        const angle = (i * 45 + rotation * 0.5) * (Math.PI / 180);
        const radius = 50 + Math.sin(i * 1.5) * 5;
        const x = 50 + radius * Math.cos(angle);
        const y = 50 + radius * Math.sin(angle);
        return (
          <div
            key={`particle-${i}`}
            className="absolute w-1 h-1 bg-purple-400/30 rounded-full"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          />
        );
      })}
    </div>
  );
}

// Timeline Item Component
interface TimelineItemProps {
  year: string;
  title: string;
  description: string;
  icon: typeof Rocket;
  highlight?: boolean;
  isLast: boolean;
}

function TimelineItem({ year, title, description, icon: Icon, highlight, isLast }: TimelineItemProps) {
  return (
    <div className="relative flex gap-6 group">
      {/* Line */}
      {!isLast && (
        <div className="absolute left-6 top-14 w-0.5 h-full bg-gradient-to-b from-purple-500/50 to-transparent" />
      )}

      {/* Icon */}
      <div className={`relative z-10 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 ${
        highlight
          ? 'bg-gradient-to-br from-purple-500 to-fuchsia-500 shadow-lg shadow-purple-500/30'
          : 'bg-bg-card border border-purple-500/30'
      }`}>
        <Icon className={highlight ? 'text-white' : 'text-purple-400'} size={20} />
      </div>

      {/* Content */}
      <div className="pb-10">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-medium text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full">
            {year}
          </span>
          <h3 className="font-semibold text-text-primary group-hover:text-purple-400 transition-colors">
            {title}
          </h3>
        </div>
        <p className="text-text-secondary text-sm leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

export function AboutPage() {
  const { t } = useTranslation();
  const [activeCountry, setActiveCountry] = useState<string | null>(null);

  // Translated timeline data
  const timelineYears = ['2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024'];
  const timeline = timelineYears.map(year => ({
    year,
    title: t(`about.timeline.items.${year}.title`),
    description: t(`about.timeline.items.${year}.description`),
    icon: timelineIcons[year],
    highlight: year === '2017' || year === '2024'
  }));

  // Translated countries data
  const countries: { name: string; code: 'tr' | 'gb' | 'fr' | 'us' | 'ca' | 'az' | 'cy' | 'ma' | 'mx'; status: string; statusKey: 'headquarters' | 'active'; region: string }[] = [
    { name: t('about.global.countries.turkey'), code: 'tr', status: t('about.global.headquarters'), statusKey: 'headquarters', region: t('about.global.regions.europeAsia') },
    { name: t('about.global.countries.uk'), code: 'gb', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.europe') },
    { name: t('about.global.countries.france'), code: 'fr', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.europe') },
    { name: t('about.global.countries.usa'), code: 'us', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.northAmerica') },
    { name: t('about.global.countries.canada'), code: 'ca', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.northAmerica') },
    { name: t('about.global.countries.azerbaijan'), code: 'az', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.caucasus') },
    { name: t('about.global.countries.northCyprus'), code: 'cy', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.mediterranean') },
    { name: t('about.global.countries.morocco'), code: 'ma', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.africa') },
    { name: t('about.global.countries.mexico'), code: 'mx', status: t('about.global.active'), statusKey: 'active', region: t('about.global.regions.latinAmerica') }
  ];

  // Translated awards data
  const awardKeys = ['tubitak', 'ieee', 'teknofest', 'google', 'aws', 'iso'];
  const awards = awardKeys.map(key => ({
    key,
    title: t(`about.awards.items.${key}.title`),
    category: t(`about.awards.items.${key}.category`),
    description: t(`about.awards.items.${key}.description`),
    year: key === 'tubitak' ? '2023' : key === 'ieee' ? '2022' : key === 'teknofest' ? '2023' : key === 'google' ? '2024' : key === 'aws' ? '2023' : '2022',
    icon: awardIcons[key]
  }));

  // Translated team highlights
  const teamHighlightKeys = ['academic', 'experience', 'publications', 'certifications'];
  const teamHighlights = teamHighlightKeys.map(key => ({
    key,
    title: t(`about.team.highlights.${key}.title`),
    count: t(`about.team.highlights.${key}.count`),
    description: t(`about.team.highlights.${key}.description`),
    icon: teamIcons[key]
  }));

  // Translated values
  const valueKeys = ['hiddenHero', 'innovation', 'reliability', 'partnership'];
  const values = valueKeys.map(key => ({
    key,
    title: t(`about.values.items.${key}.title`),
    description: t(`about.values.items.${key}.description`),
    icon: valueIcons[key].icon,
    color: valueIcons[key].color
  }));

  // Translated stats
  const stats = [
    { value: '8+', label: t('about.stats.yearsExperience') },
    { value: '50+', label: t('about.stats.successfulProjects') },
    { value: '9', label: t('about.stats.countries') },
    { value: '30+', label: t('about.stats.happyClients') }
  ];

  // Academic approach points
  const academicPoints = t('about.team.whyAcademic.points', { returnObjects: true }) as string[];

  return (
    <>
      <SEO {...SEO_CONFIG.about} />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-lg pt-32 relative overflow-hidden min-h-[80vh] flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(139, 92, 246, 0.5) 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
                <Calendar size={16} className="text-purple-400" />
                <span className="text-sm font-medium text-purple-400">{t('about.hero.badge')}</span>
              </div>

              <h1 className="heading-xl mb-6">
                {t('about.hero.title')}{' '}
                <span className="bg-gradient-to-r from-purple-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  {t('about.hero.titleHighlight')}
                </span>
              </h1>

              <p className="text-lg text-text-secondary mb-6 leading-relaxed">
                {t('about.hero.description1')}
              </p>

              <p className="text-text-secondary mb-8 leading-relaxed">
                {t('about.hero.description2')}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn btn-primary">
                  <Rocket size={18} />
                  {t('about.buttons.contact')}
                </Link>
                <Link to="/products" className="btn btn-secondary">
                  {t('about.buttons.products')}
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <AnimatedGlobe />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border-default bg-bg-surface/50">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-text-secondary text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story / Timeline Section */}
      <section className="section relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">
              {t('about.timeline.title')}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('about.timeline.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('about.timeline.description')}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {timeline.map((item, index) => (
              <TimelineItem
                key={item.year}
                year={item.year}
                title={item.title}
                description={item.description}
                icon={item.icon}
                highlight={item.highlight}
                isLast={index === timeline.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="section bg-bg-surface relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B5CF6' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('about.global.title')}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('about.global.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('about.global.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {countries.map((country) => (
              <div
                key={country.name}
                className={`card p-5 cursor-pointer transition-all duration-300 ${
                  activeCountry === country.name
                    ? 'border-purple-500/50 bg-purple-500/5 scale-[1.02]'
                    : 'hover:border-purple-500/30'
                } ${country.statusKey === 'headquarters' ? 'ring-1 ring-purple-500/20' : ''}`}
                onMouseEnter={() => setActiveCountry(country.name)}
                onMouseLeave={() => setActiveCountry(null)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-bg-elevated flex items-center justify-center overflow-hidden shadow-sm">
                    <FlagIcon code={country.code} size={36} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-text-primary">{country.name}</h3>
                      {country.statusKey === 'headquarters' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-medium">
                          {t('about.global.headquarters')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-tertiary mt-0.5">
                      {country.region}
                    </p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" title={t('about.global.active')} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-bg-card border border-border-default">
              <Globe className="text-purple-400" size={20} />
              <span className="text-text-secondary">
                <span className="text-purple-400 font-semibold">{t('about.global.continents')}</span> {t('about.global.servingIn')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="section relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl" />

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('about.awards.title')}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('about.awards.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('about.awards.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award, index) => (
              <div
                key={award.title}
                className="card group hover:border-purple-500/30 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <award.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full">
                        {award.year}
                      </span>
                      <span className="text-xs text-text-tertiary">{award.category}</span>
                    </div>
                    <h3 className="font-semibold text-text-primary mb-1 group-hover:text-purple-400 transition-colors">
                      {award.title}
                    </h3>
                    <p className="text-sm text-text-secondary">{award.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section bg-bg-surface relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5" />

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('about.team.title')}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('about.team.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('about.team.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {teamHighlights.map((item) => (
              <div key={item.title} className="card text-center group hover:border-purple-500/30 transition-all">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-500/20 transition-colors">
                  <item.icon className="text-purple-400" size={32} />
                </div>
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent mb-2">
                  {item.count}
                </div>
                <h3 className="font-semibold text-text-primary mb-2">{item.title}</h3>
                <p className="text-sm text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Team Description */}
          <div className="card p-8 bg-gradient-to-br from-bg-card to-bg-surface border-purple-500/20">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="heading-md mb-4">{t('about.team.whyAcademic.title')}</h3>
                <p className="text-text-secondary mb-4 leading-relaxed">
                  {t('about.team.whyAcademic.description')}
                </p>
                <ul className="space-y-3">
                  {academicPoints.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-text-secondary">
                      <CheckCircle2 size={16} className="text-purple-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="card p-4 text-center bg-bg-elevated">
                  <GraduationCap className="text-purple-400 mx-auto mb-2" size={32} />
                  <div className="text-xl font-bold text-text-primary">PhD & MSc</div>
                  <div className="text-xs text-text-tertiary">{t('about.team.badges.staffLevel')}</div>
                </div>
                <div className="card p-4 text-center bg-bg-elevated">
                  <BookOpen className="text-purple-400 mx-auto mb-2" size={32} />
                  <div className="text-xl font-bold text-text-primary">15+</div>
                  <div className="text-xs text-text-tertiary">{t('about.team.badges.academicPubs')}</div>
                </div>
                <div className="card p-4 text-center bg-bg-elevated">
                  <Award className="text-purple-400 mx-auto mb-2" size={32} />
                  <div className="text-xl font-bold text-text-primary">50+</div>
                  <div className="text-xs text-text-tertiary">{t('about.team.badges.certifications')}</div>
                </div>
                <div className="card p-4 text-center bg-bg-elevated">
                  <Users className="text-purple-400 mx-auto mb-2" size={32} />
                  <div className="text-xl font-bold text-text-primary">20+</div>
                  <div className="text-xs text-text-tertiary">{t('about.team.badges.expertTeam')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('about.values.title')}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('about.values.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value) => (
              <div key={value.title} className="card p-6 group hover:border-purple-500/30 transition-all">
                <div className="flex gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <value.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2 group-hover:text-purple-400 transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Quote Section */}
      <section className="section-lg bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-fuchsia-500/20 to-transparent" />

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/10 mb-8">
              <Star className="text-purple-400" size={32} />
            </div>

            <blockquote className="text-2xl md:text-3xl font-light text-text-primary mb-6 leading-relaxed">
              "{t('about.quote.text')}{' '}
              <span className="text-purple-400">{t('about.quote.highlight')}</span>{' '}
              {t('about.quote.textEnd')}"
            </blockquote>

            <p className="text-text-secondary">
              {t('about.quote.author')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-lg relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10" />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-purple-500/20 rounded-tl-3xl" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-purple-500/20 rounded-br-3xl" />

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/30">
              <Heart className="text-white" size={40} />
            </div>
            <h2 className="heading-lg mb-4">
              {t('about.cta.title')}
            </h2>
            <p className="text-text-secondary mb-8 text-lg">
              {t('about.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn btn-primary btn-lg">
                <Rocket size={20} />
                {t('about.buttons.projectTalk')}
              </Link>
              <Link to="/careers" className="btn btn-secondary btn-lg">
                {t('about.buttons.joinTeam')}
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
